import { memo, useState, useCallback, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { UserCircleIcon, BellIcon, EnvelopeIcon, Cog6ToothIcon, GiftIcon } from '@heroicons/react/24/outline'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../context/AuthContext'
import { toast } from '../../services/toastService'
import { trackButtonClick } from '../../utils/analytics'
import { useProfileManagement } from '../../hooks/useProfileManagement'
import { formatDate } from '../../utils/formatters'
import { useTranslation } from 'react-i18next'
import { createLogger } from '../../utils/logger'
import { authAPI } from '../../services/api'
import ProfileActionButtons from './profile/ProfileActionButtons'
import ProfileEditForm from './profile/ProfileEditForm'
import PasswordChangeForm from './profile/PasswordChangeForm'
import DeleteAccountForm from './profile/DeleteAccountForm'
import ProfileLocationAlerts from './profile/ProfileLocationAlerts'
import ReferralCard from './profile/ReferralCard'
import { usePushSubscription } from '../../hooks/usePushSubscription'

const logger = createLogger('ProfileSection')

/**
 * ProfileSection - User profile management component
 * 
 * Features:
 * - Avatar display with error handling
 * - Profile editing (name, email, username)
 * - Password change
 * - Account deletion
 * - Version check
 * 
 * @idempotent - Pure component with controlled state
 * @atomic - Single responsibility: user profile management (<300 lines)
 */
const ProfileSection = memo(function ProfileSection({ user, i18n, t }) {
  const { logout } = useAuth()
  
  // Profile management hook
  const {
    isEditingProfile,
    setIsEditingProfile,
    profileFormData,
    profileErrors,
    isSavingProfile,
    handleProfileChange,
    handleProfileSubmit,
    handleCancelEdit,
    avatarFile,
    avatarPreview,
    handleAvatarChange,
    handleRemoveAvatar,
    showPasswordSection,
    setShowPasswordSection,
    passwordData,
    passwordErrors,
    isChangingPassword,
    handlePasswordChange,
    handlePasswordSubmit
  } = useProfileManagement(user, i18n, t)
  
  const { isSupported: pushSupported, isSubscribed, isLoading: pushLoading, subscribe, unsubscribe } = usePushSubscription()

  const handlePushToggle = useCallback(async () => {
    trackButtonClick('push_toggle', 'profile_section', { was_subscribed: isSubscribed })
    try {
      if (isSubscribed) {
        await unsubscribe()
        toast.success(t('profile.pushDisabled') || 'Push notifications disabled')
      } else {
        await subscribe()
        toast.success(t('profile.pushEnabled') || 'Push notifications enabled!')
      }
    } catch (err) {
      const isPermissionDenied = err?.name === 'NotAllowedError'
      const isNotConfigured = err?.code === 'PUSH_NOT_CONFIGURED'
      if (isNotConfigured) return // silently ignore — VAPID not set up on this server
      toast.error(isPermissionDenied
        ? (t('profile.pushPermissionDenied') || 'Notification permission denied. Enable it in your browser settings.')
        : (t('toast.error.generic') || 'Something went wrong'))
    }
  }, [isSubscribed, subscribe, unsubscribe, t])

  const [digestEnabled, setDigestEnabled] = useState(user?.digestEnabled !== false)
  const [digestLoading, setDigestLoading] = useState(false)

  const handleDigestToggle = useCallback(async () => {
    const newValue = !digestEnabled
    setDigestLoading(true)
    trackButtonClick('digest_toggle', 'profile_section', { new_value: newValue })
    try {
      await authAPI.updateProfile({ digestEnabled: newValue })
      setDigestEnabled(newValue)
      toast.success(newValue
        ? (t('profile.digestEnabled') || 'Weekly digest enabled')
        : (t('profile.digestDisabled') || 'Weekly digest disabled')
      )
    } catch {
      toast.error(t('common.errorOccurred') || 'Something went wrong')
    } finally {
      setDigestLoading(false)
    }
  }, [digestEnabled, t])

  // Auto-unsubscribe from digest when ?unsubscribe=digest is in the URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('unsubscribe') === 'digest' && digestEnabled) {
      setDigestLoading(true)
      authAPI.updateProfile({ digestEnabled: false }).then(() => {
        setDigestEnabled(false)
        toast.success(t('profile.digestDisabled') || 'Weekly digest disabled')
      }).catch(() => {
        toast.error(t('common.errorOccurred') || 'Something went wrong')
      }).finally(() => {
        setDigestLoading(false)
      })
    }
  // Only run on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Delete account state
  const [showDeleteSection, setShowDeleteSection] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [isDeletingAccount, setIsDeletingAccount] = useState(false)
  
  // Settings panel collapsed by default
  const [showSettings, setShowSettings] = useState(false)

  // Avatar error state
  const [avatarError, setAvatarError] = useState(false)

  // Handle account deletion
  const handleDeleteAccount = async (e) => {
    e.preventDefault()
    
    if (deleteConfirmText !== 'DELETE') {
      trackButtonClick('delete_account_invalid_confirmation', 'profile_tab', { 
        entered_text: deleteConfirmText,
        expected_text: 'DELETE'
      })
      toast.error(t('profile.typeDeleteToConfirm'))
      return
    }
    
    trackButtonClick('delete_account_submit', 'profile_tab', { 
      user_id: user.id,
      confirmation_valid: true
    })
    
    setIsDeletingAccount(true)
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/profile`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error('Failed to delete account')
      }
      
      trackButtonClick('delete_account_success', 'profile_tab', { 
        user_id: user.id
      })
      
      toast.success(t('profile.accountDeleted'))
      
      setTimeout(() => {
        logout()
      }, 1500)
    } catch (error) {
      logger.error('Error deleting account', error, { userId: user?.id })
      trackButtonClick('delete_account_error', 'profile_tab', { 
        user_id: user.id,
        error_message: error.message
      })
      toast.error(t('toast.error.accountDeleteFailed'))
      setIsDeletingAccount(false)
    }
  }

  return (
    <div className="space-y-3">
      {/* Profile Header */}
      <div className="bg-primary-50 rounded-xl p-4 shadow-sm border border-primary-100">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="h-16 w-16 rounded-full bg-white shadow-lg flex items-center justify-center flex-shrink-0 overflow-hidden ring-2 ring-white">
              {user?.avatar && user.avatar.trim() !== '' && !avatarError ? (
                <img 
                  src={user.avatar} 
                  alt={`${user.firstName}'s avatar`}
                  className="h-full w-full object-cover"
                  crossOrigin="anonymous"
                  referrerPolicy="no-referrer"
                  onError={() => setAvatarError(true)}
                />
              ) : (
                <UserCircleIcon className="h-12 w-12 text-gray-400" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-gray-900 truncate">
                {user.firstName} {user.lastName}
              </h2>
              <p className="text-xs text-gray-600 truncate mt-0.5">{user.email}</p>
              <p className="text-xs text-gray-500 mt-1">
                {t('profile.memberSince')} {formatDate(user.createdAt || new Date().toISOString(), i18n.language)}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <ProfileActionButtons
          isEditingProfile={isEditingProfile}
          showPasswordSection={showPasswordSection}
          showDeleteSection={showDeleteSection}
          hasGoogleId={user.hasGoogleId || false}
          onEditToggle={() => {
            setIsEditingProfile(!isEditingProfile)
            setShowPasswordSection(false)
            setShowDeleteSection(false)
          }}
          onPasswordToggle={() => {
            setShowPasswordSection(!showPasswordSection)
            setIsEditingProfile(false)
            setShowDeleteSection(false)
          }}
          onDeleteToggle={() => {
            setShowDeleteSection(!showDeleteSection)
            setIsEditingProfile(false)
            setShowPasswordSection(false)
            setDeleteConfirmText('')
          }}
          onLogout={logout}
          userId={user.id}
          t={t}
        />

        {/* Edit Profile Form */}
        {isEditingProfile && !showPasswordSection && !showDeleteSection && (
          <ProfileEditForm
            user={user}
            formData={profileFormData}
            errors={profileErrors}
            isSaving={isSavingProfile}
            avatarPreview={avatarPreview}
            avatarError={avatarError}
            onSubmit={handleProfileSubmit}
            onChange={handleProfileChange}
            onCancel={handleCancelEdit}
            onAvatarChange={handleAvatarChange}
            onRemoveAvatar={handleRemoveAvatar}
            onAvatarError={setAvatarError}
            t={t}
            i18n={i18n}
          />
        )}

        {/* Change Password Form */}
        {showPasswordSection && !isEditingProfile && !showDeleteSection && (
          <PasswordChangeForm
            passwordData={passwordData}
            errors={passwordErrors}
            isChanging={isChangingPassword}
            onSubmit={handlePasswordSubmit}
            onChange={handlePasswordChange}
            onCancel={() => setShowPasswordSection(false)}
            t={t}
          />
        )}

        {/* Delete Account Form */}
        {showDeleteSection && (
          <DeleteAccountForm
            confirmText={deleteConfirmText}
            isDeleting={isDeletingAccount}
            onConfirmChange={setDeleteConfirmText}
            onSubmit={handleDeleteAccount}
            onCancel={() => {
              trackButtonClick('cancel_delete_account', 'profile_tab')
              setShowDeleteSection(false)
              setDeleteConfirmText('')
            }}
            t={t}
          />
        )}
      </div>

      {/* Notifications + Alerts + Referral — collapsed under a Settings toggle */}
      <div className="bg-white rounded-xl border border-gray-200/70 shadow-sm overflow-hidden">
        <button
          onClick={() => setShowSettings(s => !s)}
          onTouchEnd={(e) => { e.preventDefault(); setShowSettings(s => !s) }}
          className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Cog6ToothIcon className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">{t('nav.account', 'Account Settings')}</span>
          </div>
          {showSettings
            ? <ChevronUpIcon className="h-4 w-4 text-gray-400" />
            : <ChevronDownIcon className="h-4 w-4 text-gray-400" />
          }
        </button>

        {showSettings && (
          <div className="border-t border-gray-100">
            {/* Notifications settings */}
            {(pushSupported || true) && (
              <div className="divide-y divide-gray-100">
                {pushSupported && (
                  <div className="px-4 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <BellIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900">{t('profile.pushNotifications') || 'Push Notifications'}</p>
                        <p className="text-xs text-gray-500">{t('profile.pushNotificationsDesc') || 'Get notified instantly when a vendor is spotted'}</p>
                      </div>
                    </div>
                    <button
                      onClick={handlePushToggle}
                      onTouchEnd={handlePushToggle}
                      disabled={pushLoading}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:opacity-50 flex-shrink-0 ${isSubscribed ? 'bg-primary' : 'bg-gray-300'}`}
                      aria-label={isSubscribed ? 'Disable push notifications' : 'Enable push notifications'}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isSubscribed ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                )}

                <div className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <EnvelopeIcon className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{t('profile.weeklyDigest') || 'Weekly Digest'}</p>
                      <p className="text-xs text-gray-500">{t('profile.weeklyDigestDesc') || 'A weekly roundup of sightings from your favorites'}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleDigestToggle}
                    onTouchEnd={handleDigestToggle}
                    disabled={digestLoading}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 disabled:opacity-50 flex-shrink-0 ${digestEnabled ? 'bg-primary' : 'bg-gray-300'}`}
                    aria-label={digestEnabled ? 'Disable weekly digest' : 'Enable weekly digest'}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${digestEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>
            )}

            {/* Nearby alerts */}
            {pushSupported && (
              <div className="border-t border-gray-100">
                <ProfileLocationAlerts user={user} />
              </div>
            )}

            {/* Referral link */}
            <div className="border-t border-gray-100 px-4 py-3">
              <ReferralCard />
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

export default ProfileSection
